import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-sm font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-brand-cta text-brand-cta-foreground border border-brand-cta-border shadow-sm hover:bg-brand-cta-hover",
        destructive:
          "bg-brand-cta-destructive-bg text-brand-cta-destructive-fg border border-brand-cta-destructive-border shadow-sm hover:bg-brand-cta-destructive-hover",
        outline:
          "bg-transparent text-brand-cta-outline-fg border border-brand-cta-outline-border hover:bg-brand-cta-outline-hover",
        secondary:
          "bg-brand-cta-secondary-bg text-brand-cta-secondary-fg border border-brand-cta-secondary-border hover:bg-brand-cta-secondary-hover",
        ghost:
          "rounded-md hover:bg-accent hover:text-accent-foreground",
        link: "rounded-md text-primary underline-offset-4 hover:underline",
        cream:
          "rounded-md border border-[#E8DFD4]/60 bg-[#FAF6F1] text-foreground hover:bg-[#F5EDE3]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        // #1249 — icon default bumped h-10 (40px) → h-11 (44px) to
        // meet WCAG 2.1 AA Success Criterion 2.5.5 (Target Size).
        // Callsites that genuinely need the old 40px (dense desktop
        // toolbars) can pass `className="h-10 w-10"` to override.
        icon: "h-11 w-11",
        cream: "h-8 rounded-md px-3 text-xs font-medium gap-1.5 [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
